import { DimensionAggregationStrategies } from 'business/definitions';
import { isCurrentlyOnPublicDashboard } from 'helpers/dashboardHelpers';
import qs from 'query-string';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import APICall, { HttpContentWithData } from 'store/APIClient';
import { NavigationTree } from '../types';

/**
 * This hook is used to fecth all the trees a user can dig in. It fetchs all the leaves from the root.
 * To determine on which dimension one can dig in, we use the `aggregationStrategy` field value.
 * @param chartKey - The key of the chart
 * @param dimensions - The dimensions of the chart
 * @returns The list of all the trees a user can dig in
 */
export function useGetNavigationTree(
    chartKey: number,
    dimensions: QueryDimensionT[],
): UseQueryResult<NavigationTree[]> {
    const isOnPublicDashboard = isCurrentlyOnPublicDashboard();

    const fields = dimensions
        .filter(
            (f) =>
                f.aggregationStrategy === DimensionAggregationStrategies.tree,
        )
        .map((f) => f.field);

    const queryString = qs.stringify({ fields });
    const endpoint = `graph/get_navigation_tree?${queryString}`;

    return useQuery<NavigationTree[]>({
        queryKey: ['navigationTree', chartKey, queryString],

        queryFn: async () => {
            const response = await APICall<
                HttpContentWithData<NavigationTree[]>
            >({
                endpoint,
                method: 'GET',
                throwWhenNotOk: false,
            });
            return response.content.data;
        },

        enabled: fields.length > 0 && !isOnPublicDashboard,
        staleTime: 60 * 5,
    });
}
